import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  FinanceContactByTaxIdDocument,
  FinanceValueObjectsContactTaxIdTypeEnum,
  UpdateFinanceContactDetailsDocument,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ContactTaxIdTypeSelector } from 'modules/finance/activities/financeContactCreate/_ContactTaxIdTypeSelector'

interface ContactDetailsFormValues {
  id: number
  name: string
  taxId?: string
  taxIdType?: FinanceValueObjectsContactTaxIdTypeEnum
  legalName?: string
  website?: string
  email?: string
  phoneNumber?: string
}

interface Props {
  contact: ContactFragment
  onSuccess?: () => void
  editable?: boolean
}

const ContactDetailsForm: React.FC<Props> = ({ contact, onSuccess, editable = true }) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [checkTaxId, setCheckTaxId] = React.useState<string | undefined>(
    contact?.taxId || undefined
  )
  const { company } = useCurrentSession()

  const [updateContactDetails] = useMutation(UpdateFinanceContactDetailsDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceContact,
  })

  const { values, errors, setErrors, setValues, handleSubmit, isValid, dirty, touched } =
    useForm<ContactDetailsFormValues>({
      enableReinitialize: true,

      defaultValues: {
        id: contact.id,
        taxId: contact.taxId ?? '',
        taxIdType: contact.taxIdType ?? undefined,
        name: contact.name ?? '',
        legalName: contact.legalName ?? '',
        website: contact.website ?? '',
        email: contact.email ?? '',
        phoneNumber: contact.phoneNumber ?? '',
      },

      validate: (data) => {
        const errors: FormErrors<ContactDetailsFormValues> = {}

        if (!data.name) {
          errors.name = i18n.t('common.empty')
        }

        return errors
      },

      onSubmit: async (values) => {
        setSubmitting(true)

        const variables = {
          id: values.id,
          companyId: company.id,
          taxId: values.taxId,
          taxIdType: values.taxIdType,
          name: values.name,
          legalName: values.legalName,
          website: values.website ?? '',
          email: values.email ?? '',
          phoneNumber: values.phoneNumber ?? '',
        }

        const result = await updateContactDetails({ variables })

        const handled = handleResult(result, (errors) => {
          if (errors) {
            setErrors(errors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.contacts.contact_form.update_contact.messages.error'),
            })
          }
        })

        setSubmitting(false)

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('finance.contacts.contact_form.update_contact.messages.success'),
          })

          onSuccess?.()
        }
        return handled
      },
    })

  const { data } = useQuery(FinanceContactByTaxIdDocument, {
    variables: { companyId: company.id, taxId: checkTaxId || '' },
    skip: !company.id || !checkTaxId,
  })

  const existingContact = React.useMemo(() => {
    if (data) {
      const fetchContact = data.finance.contactsConnection.nodes[0]
      return fetchContact && fetchContact.id !== contact?.id ? fetchContact : undefined
    }
    return undefined
  }, [data, contact?.id])

  const taxIdError = existingContact
    ? i18n.t('finance.contacts.contact_form.errors.tax_id_uniqueness_error', {
        contact_name: existingContact.name,
      })
    : undefined

  const validateTaxId = (taxId: string) => {
    setCheckTaxId(taxId)
  }

  const debouncedValidation = debounce(validateTaxId, 500)

  return (
    <>
      <Box gap="s24">
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.name')}
          value={values.name}
          onChange={(value) => setValues({ ...values, name: value })}
          mandatory
          error={touched.name ? errors.name : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.legal_name')}
          value={values.legalName}
          onChange={(value) => setValues({ ...values, legalName: value })}
          error={touched.legalName ? errors.legalName : undefined}
          readonly={!editable}
        />
        <ContactTaxIdTypeSelector
          selectedTaxIdType={values.taxIdType}
          onSelectTaxIdType={(value) => setValues({ ...values, taxIdType: value })}
          readOnly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.tax_identification_number')}
          value={values.taxId}
          onChange={(value) => {
            setValues({ ...values, taxId: value })
            if (editable) {
              debouncedValidation(value)
            }
          }}
          error={taxIdError || (touched.taxId ? errors.taxId : undefined)}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.website')}
          value={values.website}
          onChange={(value) => setValues({ ...values, website: value })}
          error={touched.website ? errors.website : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.email')}
          value={values.email}
          onChange={(value) => setValues({ ...values, email: value })}
          error={touched.email ? errors.email : undefined}
          readonly={!editable}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.phone_number')}
          value={values.phoneNumber}
          onChange={(value) => setValues({ ...values, phoneNumber: value })}
          error={touched.phoneNumber ? errors.phoneNumber : undefined}
          readonly={!editable}
        />
        {(!contact || dirty) && (
          <Box alignItems="flexEnd">
            <Button onClick={() => handleSubmit()} disabled={!editable || !isValid || submitting}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

export default ContactDetailsForm
